<template>
  <v-expansion-panels multiple focusable v-model="panel">
    <v-expansion-panel v-for="(attestation, i) in studentCourseAttestations" :key="i">
      <v-expansion-panel-header
        :class="headerClass(attestation, i)"
        @click.native.stop="preventExpand(i)"
        :disabled="isAttestationDisabled(attestation)"
      >
        <v-row no-gutters>
          <v-col md="4">Bilan {{ attestation.typeTranslated }}</v-col>
          <v-col md="3">
            <div v-if="isLatestAttestation(attestation) && !isEditMode(i)" class="text-right px-5">
              <v-btn
                :href="`/pdf/bilan/${student_course_id}`"
                fab
                dark
                x-small
                elevation="1"
                color="blue"
                target="_blank"
              >
                <v-icon dark>mdi-file-pdf-box</v-icon>
              </v-btn>
              <v-btn
                class="ml-5"
                :href="`/pdf/bilan/${student_course_id}?see`"
                fab
                dark
                elevation="1"
                x-small
                color="blue lighten-2"
                target="_blank"
              >
                <v-icon dark>mdi-text-box</v-icon>
              </v-btn>
            </div>
          </v-col>
          <v-col md="5" class="text-right pr-10">
            <v-btn v-if="isEditMode(i)" color="blue" @click.native.stop="onSave(attestation, i)" dark small>
              Sauvegarder
            </v-btn>
            <v-btn
              :color="attestation.created_at ? `green` : `blue`"
              @click.native.stop="startEditing(i)"
              v-if="(isAdmin || isProfessor) && !isEditMode(i)"
              dark
              small
              :disabled="isAttestationDisabled(attestation)"
            >
              {{ attestation.created_at ? "Modifier" : "Ajouter" }}
            </v-btn>
            <v-btn
              v-if="isEditMode(i) && attestation.id"
              color="error"
              @click.native.stop="onDelete(attestation)"
              small
            >
              Supprimer
            </v-btn>
            <v-btn @click.native.stop="stopEditing" v-if="isEditMode(i)" small>Annuler</v-btn>
          </v-col>
        </v-row>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <attestation-form
          :ref="'attestationForm' + i"
          :attestation="attestation"
          :is-edit-mode="isEditMode(i)"
        />
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>
<script>
import { mapGetters } from "vuex"
import AttestationForm from "@/components/attestations/AttestationForm"
import Attestation from "@/models/Attestation"
import { notify } from "@/services/notification"

export default {
  components: {
    AttestationForm,
  },
  props: {
    student_course_id: {
      required: true,
    },
  },
  mounted() {
    this.fetchAttestations().then(() => {
      this.openExistingAttestationPanels()
    })
  },
  data() {
    return {
      studentCourseAttestations: [],
      panel: [],
      editingPanelId: null,
      loading: false,
      formTitle: "Modifier Résultats",
    }
  },
  watch: {
    student_course_id: async function () {
      this.stopEditing()
      this.panel = []
      this.fetchAttestations().then(() => {
        this.openExistingAttestationPanels()
      })
    },
  },
  methods: {
    isEditMode(panelId) {
      return this.editingPanelId === panelId
    },
    headerClass(attestation, i) {
      if (this.isAttestationDisabled(attestation)) {
        return "grey darken-1"
      }

      if (this.isEditMode(i)) {
        return "orange lighten-1"
      }

      if (attestation.type === "final") {
        return attestation.created_at ? "green lighten-3" : "green lighten-5"
      }

      if (attestation.type !== "final") {
        return attestation.created_at ? "red lighten-3" : "red lighten-5"
      }

      return "grey darken-1"
    },
    isAttestationDisabled(attestation) {
      return !attestation.created_at && this.hasFinalAttestation && attestation.type !== "final"
    },
    isLatestAttestation(attestation) {
      if (!this.studentCourseAttestations.length) {
        return false
      }

      const lastAttestation = this.studentCourseAttestations.reduce((acc, curr) => {
        if (!acc.date) return curr
        if (!curr.date) return acc
        return acc.date > curr.date ? acc : curr
      }, {}) 

      if (!lastAttestation?.date) {
        return false
      }

      return attestation.date === lastAttestation.date
    },
    startEditing(i) {
      this.editingPanelId = i
      this.panel = [i]
    },
    stopEditing() {
      this.editingPanelId = null
      this.fetchAttestations()
    },
    fetchAttestations() {
      if (this.student_course_id) {
        this.loading = true
        return Attestation.getAttestationsByStudentCourseId(this.student_course_id)
          .then((result) => {
            this.studentCourseAttestations = result
            return result
          })
          .catch((error) => {
            console.log(error)
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    async onSave(attestation, index) {
      const formRefName = `attestationForm${index}`
      const formComponent = this.$refs[formRefName][0]
      if (!formComponent) {
        console.error("Form component not found.")
        return
      }

      if (this.isAttestationDisabled(attestation)) {
        notify("error", "Cannot save attestation...")
        return
      }

      const isValid = formComponent.validateForm()
      if (!isValid) {
        notify("error", "Validation failed.")
        return
      }

      this.loading = true
      await Attestation.upsertAttestation(attestation)
      this.fetchAttestations()
      this.stopEditing()
      this.loading = false
    },
    preventExpand(i) {
      if (this.editingPanelId !== null) {
        this.stopEditing()
      }

      if (!this.studentCourseAttestations[i].created_at) {
        if (this.panel.includes(i)) {
          this.startEditing(i)
        }
      }
    },
    openExistingAttestationPanels() {
      this.studentCourseAttestations.forEach((attestation, i) => {
        if (attestation.created_at) {
          this.panel.push(i)
        }
      })
    },
    async onDelete(attestation) {
      if (confirm("Êtes-vous sûr de vouloir supprimer cette attestation ?")) {
        await Attestation.deleteAttestation(attestation.id)
        this.stopEditing()
        this.panel = []
        this.fetchAttestations()
      }
    },
  },
  computed: {
    ...mapGetters("Auth", ["isAdmin", "isProfessor"]),
    hasFinalAttestation() {
      return this.studentCourseAttestations.some(
        (attestation) => attestation.type === "final" && attestation.created_at
      )
    },
  },
}
</script>
